import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { CookieAdapterService } from '../../core/cookie-adapter.service';
import { LiveAgentSupportService } from '../../header/live-agent-support/live-agent-support.service';
import { LocationService } from '../../shared/location.service';
import { OeAppService } from '../../shared/oe-app.service';
import { TranslationService } from '../../shared/translation.service';
import { LoginDto, LoginErrorReason } from '../../shared/types/LoginDto';
import { PhoneTranslationType } from '../../shared/types/PhoneTranslationType';
import { WindowService } from '../../shared/window.service';
import { interpolateLinks } from '../../shared/helpers/interpolate-links';
import { Login } from '../login';
import { LoginService } from '../login.service';
import { SharedAccountService } from '../shared-account.service';

const SAVED_EMAIL_COOKIE_KEY = 'oeusername';
const LOGIN_ATTEMPT_COOKIE_KEY = '__oe_lattempt';
const HIDE_LOGIN_PANEL_KEY = 'hide_login_panel';

export enum LoginStep {
  EMAIL = 1,
  PASSWORD = 2,
}

@Component({
  selector: 'lp2-login-box',
  templateUrl: './login-box.component.html',
  styleUrls: ['./login-box.component.scss'],
})
export class LoginBoxComponent implements OnInit {
  loginModel = new Login('', '', false);
  isLoading: boolean;
  attemptNo: number;

  @ViewChild('errorModalTemplate') errorModalTemplate: ElementRef;
  @ViewChild('resetPasswordModalTemplate') resetPasswordModalTemplate: ElementRef;
  @ViewChild('b2gModalTemplate') b2gModalTemplate: ElementRef;
  @ViewChild('chatOnline') chatOnline: ElementRef;
  @ViewChild('passwordInput') passwordInput: ElementRef;

  isMobile: boolean;

  modalTitle: string;
  modalMessage: string;
  modalType: string;

  isCaptchaValid: boolean;
  wrongEmail: boolean;
  wrongPassword: boolean;
  cachedCountryDto: PhoneTranslationType;
  clientId: string;
  isPasswordSetupSuccess: boolean;

  modalSettings: NgbModalOptions = {
    backdrop: 'static',
    windowClass: 'login-error',
  };

  b2gModalSettings: NgbModalOptions = {
    backdrop: 'static',
    centered: true,
  };

  hidePanel = false;

  LoginStep = LoginStep;
  loginStep = LoginStep.EMAIL;
  isCheckingB2G = false;
  b2gMessageKey: string;

  constructor(
    private loginService: LoginService,
    private windowService: WindowService,
    private cookieService: CookieAdapterService,
    private modalService: NgbModal,
    private locationService: LocationService,
    private oeAppService: OeAppService,
    private translationService: TranslationService,
    private liveAgentSupportService: LiveAgentSupportService,
    private sharedAccountService: SharedAccountService
  ) {}

  ngOnInit() {
    const query = this.windowService.getSearchParams();
    const loginError = query.get('login_error');
    this.clientId = query.get('clientId');

    if (loginError && this.clientId) {
      this.handleSSOLoginError();
    } else {
      this.handleManualLoginOpts();
    }
  }

  validateCaptcha(response) {
    this.isCaptchaValid = response != null;
  }

  submitValid() {
    return !this.isLoading && (this.attemptNo < 3 || this.isCaptchaValid);
  }

  isChatAvailable() {
    return this.chatOnline.nativeElement.style.display !== 'none';
  }

  enterLiveChat() {
    this.liveAgentSupportService.startChat();
  }

  isEmailValid(): boolean {
    const control = new FormControl(this.loginModel.email);
    return Validators.required(control) === null && Validators.email(control) === null;
  }

  onEmailFocus() {
    if (this.loginStep === LoginStep.PASSWORD) {
      this.loginStep = LoginStep.EMAIL;
      this.loginModel.password = '';
    }
  }

  onEnterPress(event: Event) {
    if (this.loginStep === LoginStep.EMAIL) {
      event.preventDefault();
      if (this.isEmailValid() && !this.isCheckingB2G) {
        this.onNextClick();
      }
    }
  }

  onNextClick() {
    this.isCheckingB2G = true;
    this.loginService.checkB2GLogin(this.loginModel.email).subscribe(
      (res) => {
        this.isCheckingB2G = false;
        if (res.messageKey) {
          this.b2gMessageKey = res.messageKey;
          this.modalService.open(this.b2gModalTemplate, this.b2gModalSettings);
          return;
        }
        this.resetErrors();
        this.goToPasswordStep();
      },
      () => {
        this.isCheckingB2G = false;
        this.resetErrors();
        this.goToPasswordStep();
      }
    );
  }

  private goToPasswordStep() {
    this.loginStep = LoginStep.PASSWORD;
    setTimeout(() => {
      if (this.passwordInput && this.passwordInput.nativeElement) {
        this.passwordInput.nativeElement.focus();
      }
    });
  }

  login() {
    this.isLoading = true;
    const model = this.loginModel;
    this.resetErrors();
    this.loginService.loginWithModel(model).subscribe(
      (res) => {
        if (res.success) {
          this.cookieService.remove(LOGIN_ATTEMPT_COOKIE_KEY);
          if (model.rememberMe) {
            this.cookieService.put(SAVED_EMAIL_COOKIE_KEY, model.email);
          } else {
            this.cookieService.remove(SAVED_EMAIL_COOKIE_KEY);
          }
          if (res.accountSharing) {
            this.sharedAccountService.accountSharingGuard(true, res.url);
            return;
          }
          this.windowService.redirectToUrl(res.url);
        } else {
          this.isLoading = false;
          this.clearPassword();
          this.handleError(res);
          const areAttemptsEven = this.attemptNo % 2 === 0;

          if (this.attemptNo > 2 && areAttemptsEven) {
            this.openResetPasswordSupportModal();
          }
        }
      },
      (err) => {
        console.error('ERROR at login', err);
        this.windowService.redirectToUrl('');
      }
    );
  }

  resetErrors() {
    this.wrongEmail = false;
    this.wrongPassword = false;
  }

  clearPassword() {
    this.loginModel.password = '';
  }

  handleError(error: LoginDto) {
    let message;

    switch (error.reason) {
      case LoginErrorReason.EMAIL_ERROR:
        this.attemptNo = error.attempt;
        this.cookieService.put(LOGIN_ATTEMPT_COOKIE_KEY, this.attemptNo.toString());
        this.wrongEmail = true;
        break;
      case LoginErrorReason.PASSWORD_ERROR:
        this.attemptNo = error.attempt;
        this.cookieService.put(LOGIN_ATTEMPT_COOKIE_KEY, this.attemptNo.toString());
        this.wrongPassword = true;
        break;
      case LoginErrorReason.OESUSPENDED:
        message = 'login2.account.status.OESuspended.text';
        this.openErrorModal('login.status.OESuspended.TITLE', message, error.countryCode);
        break;
      case LoginErrorReason.STUDENTPAUSED:
        message = 'login2.status.studentPaused.text';
        this.openErrorModal('login.status.studentPaused.TITLE', message, error.countryCode);
        break;
      case LoginErrorReason.OECANCELED:
        message = 'login2.status.OECancelled.text';
        this.openErrorModal('login.status.OECanceled.TITLE', message, error.countryCode);
        break;
      case LoginErrorReason.CUSTOMERCANCELLED:
      case LoginErrorReason.FAILEDSALE:
        message = 'login2.status.customerCancelled.text';
        this.openErrorModal('login.status.customerCanceled.TITLE', message, error.countryCode);
        break;
      case LoginErrorReason.SERVICECOMPLETE:
        message = 'login2.status.serviceComplete.text';
        this.openErrorModal('login.status.serviceComplete.TITLE', message, error.countryCode);
        break;
      case LoginErrorReason.B2B_ERROR:
        message = this.getB2bErrorKey(error.clientId);
        this.openErrorModal(null, message, error.clientId);
        break;
      default:
        message = 'login2.status.OECancelled.text';
        this.openErrorModal('login.status.OECanceled.TITLE', message, error.countryCode);
    }
  }

  getPhoneWithCountryCode(countryCode) {
    return this.locationService.getPhoneWithCountryCode(countryCode);
  }

  openErrorModal(title: string | null, message: string, countryCode: string) {
    this.modalTitle = title;
    this.modalMessage = message;
    if ((!this.cachedCountryDto || !this.cachedCountryDto.phoneNumber) && !this.clientId) {
      this.locationService.getPhoneWithCountryCode(countryCode).subscribe((res) => {
        this.cachedCountryDto = { phoneNumber: res.phone.oeServicePhone };
        this.modalService.open(this.errorModalTemplate, this.modalSettings);
      });
    } else {
      this.modalService.open(this.errorModalTemplate, this.modalSettings);
    }
  }

  openResetPasswordSupportModal() {
    this.modalService.open(this.resetPasswordModalTemplate, this.modalSettings);
  }

  closePanel() {
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 30);
    this.hidePanel = true;
    this.cookieService.put(HIDE_LOGIN_PANEL_KEY, JSON.stringify(true), expirationDate);
  }

  private handleSSOLoginError() {
    this.handleError({
      success: false,
      reason: LoginErrorReason.B2B_ERROR,
      clientId: this.clientId,
    });
  }

  private handleManualLoginOpts() {
    const savedEmail = this.cookieService.get(SAVED_EMAIL_COOKIE_KEY);
    const attempt = this.cookieService.get(LOGIN_ATTEMPT_COOKIE_KEY);
    const hidePanel = this.cookieService.get(HIDE_LOGIN_PANEL_KEY);
    this.attemptNo = parseInt(attempt, 10) || 0;
    this.isMobile = this.oeAppService.isMobileDevice();
    const searchParams = this.windowService.getSearchParams();
    this.isPasswordSetupSuccess = searchParams.get('passwordsetup') === 'success';

    if (hidePanel && JSON.parse(hidePanel)) {
      this.hidePanel = true;
    }

    if (savedEmail) {
      this.loginModel.rememberMe = true;
      this.loginModel.email = savedEmail;
    }
  }

  private getB2bErrorKey(clientId: string) {
    const defaultKey = 'login2.status.b2bAccessDeniedText.Default';
    const clientIdKey = `login2.status.b2bAccessDeniedText.${clientId}`;
    const clientIdTranslation = this.translationService.translate(clientIdKey);
    const hasTranslation = clientIdTranslation !== clientIdKey;
    return hasTranslation ? clientIdTranslation : defaultKey;
  }

  get b2gModalMessage(): string {
    const text = this.translationService.translate(`${this.b2gMessageKey}.text`);
    const url = this.translationService.translate(`${this.b2gMessageKey}.url`);
    return interpolateLinks(text, [url]);
  }
}



// WEBPACK FOOTER //
// ./ts/app/login/login-box/login-box.component.ts